import { Component, NgZone, OnInit } from '@angular/core';
import {
  ApexAnnotations,
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';
import {
  ApiService,
  TeamSkillData,
  TeamSkillEntry,
  UserKnowledge,
} from '../../services/api.service';
import { NgClass } from '@angular/common';
import { MultiSelect } from 'primeng/multiselect';
import { SkillmapPanelComponent } from '../skillmap-panel/skillmap-panel.component';
import { TableService } from '../../services/table.service';
import { FormsModule } from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';

@Component({
  selector: 'app-skillmap-like',
  standalone: true,
  imports: [
    NgApexchartsModule,
    NgClass,
    MultiSelect,
    SkillmapPanelComponent,
    FormsModule,
    PrimeTemplate,
    ButtonDirective,
    ProgressSpinner,
  ],
  templateUrl: './skillmap.component.html',
  styleUrls: ['./skillmap.component.scss'],
})
export class SkillmapLikeComponent implements OnInit {
  chartOptions: {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    dataLabels: ApexDataLabels;
    title: ApexTitleSubtitle;
    annotations: ApexAnnotations;
    tooltip: ApexTooltip;
  } = {} as any;

  userKnowledgeData!: UserKnowledge[];
  showPanel = false;
  selectedDataLabel = '';
  teamSkillEntry?: TeamSkillEntry;
  categories: string[] = [];
  selectedCategories: string[] = [];
  private skillToTeamsMap?: TeamSkillEntry[] | undefined;

  labelSize: number = 14;

  constructor(
    private apiService: ApiService,
    private tableService: TableService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.loadCategories();
    this.loadChartData();
  }

  showPanelIsFalse() {
    this.showPanel = false;
  }

  private loadCategories(): void {
    this.tableService.getSkills().subscribe(skills => {
      const uniqueCategories = new Set(skills.map(s => s.g_name));
      this.categories = Array.from(uniqueCategories);
    });
  }

  protected loadChartData(): void {
    this.apiService.getLikes().subscribe(allLikesData => {
      this.tableService.getSkills().subscribe(skillMeta => {
        const filteredSkillNames = skillMeta
          .filter(
            skill =>
              this.selectedCategories.length === 0 ||
              this.selectedCategories.includes(skill.g_name)
          )
          .map(skill => skill.skill_name);

        const filteredData: Record<
          string,
          { like: number; dislike: number; tot: number }
        > = {};
        for (const key of filteredSkillNames) {
          if (allLikesData[key]) {
            filteredData[key] = allLikesData[key];
          }
        }

        this.apiService
          .getSkillTeamMap()
          .subscribe((teamSkillData: TeamSkillData) => {
            this.skillToTeamsMap = teamSkillData.data;
            const totalActiveTeams = teamSkillData.activeTeamCount;

            const series = this.buildChartSeries(filteredData);
            this.setupChart(series);
          });
      });
    });
  }

  private mapSkillsToTeams(
    data: TeamSkillData['data']
  ): Record<string, Set<string>> {
    const map: Record<string, Set<string>> = {};

    for (const { skill, teams } of data) {
      map[skill] = new Set(teams);
    }

    return map;
  }

  private buildChartSeries(
    allLikesData: Record<string, { like: number; dislike: number; tot: number }>
  ): any[] {
    return Object.keys(allLikesData).map(skillName => {
      const { like = 0, dislike = 0, tot = 1 } = allLikesData[skillName] ?? {};

      return {
        x: (like / tot) * 100,
        y: (dislike / tot) * 100, // Add a small offset to avoid overlap?

        meta: { label: skillName },
      };
    });
  }

  private setupChart(series: any[]): void {
    this.chartOptions = {
      series: [
        {
          name: 'Skill Level',
          data: series,
        },
      ],
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        width: '100%',
        height: '100%',
        type: 'scatter',
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const selected =
              config.w.config.series[config.seriesIndex].data[
                config.dataPointIndex
              ];
            this.handlePointSelection(selected.meta.label);
          },
        },
      },
      dataLabels: {
        style: {
          colors: ['#4df5bdff'],
          fontSize: '10px',
        },
        background: {
          padding: 12,
          foreColor: '#000',
          borderRadius: 4,
        },
        enabled: true,
        formatter: (_val, opt) =>
          opt.w.globals.initialSeries[0].data[opt.dataPointIndex].meta.label,
      },
      xaxis: {
        min: 0,
        max: 100,
        tickAmount: 10,
        labels: {
          formatter: val => `${Math.round(Number(val))}`,
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 10,
        labels: {
          formatter: val => `${Math.round(val)}`,
        },
      },
      title: {
        text: 'Skillmap Chart',
      },
      annotations: {
        xaxis: [
          {
            x: 50,
            borderColor: '#000',
            borderWidth: 2,
            strokeDashArray: 2,
            label: { text: 'Dislikes' },
          },
        ],
        yaxis: [
          {
            y: 50,
            borderColor: '#000',
            borderWidth: 2,
            strokeDashArray: 2,
            label: { text: 'Likes' },
          },
        ],
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const point = w.config.series[seriesIndex].data[dataPointIndex];
          return `
          <div class="tooltip">
            <p><strong>${point.meta.label}</strong></p>
            <p>Likes <strong>${Math.round(point.x)}%</strong></p>
            <p>Dislikes <strong>${Math.round(point.y)}%</strong></p>
          </div>`;
        },
      },
    };
  }

  private handlePointSelection(skillName: string): void {
    this.ngZone.run(() => {
      this.showPanel = true;

      this.teamSkillEntry = this.skillToTeamsMap?.find(
        st => st.skill == skillName
      );

      this.selectedDataLabel = skillName;
      this.apiService.getSkillUserLikes(skillName).subscribe(data => {
        this.userKnowledgeData = data;
      });
    });
  }

  increaseLabelSize() {
    if (this.labelSize < 20) {
      this.labelSize++;
      this.chartOptions = {
        ...this.chartOptions,
        dataLabels: {
          ...this.chartOptions.dataLabels,
          style: {
            ...this.chartOptions.dataLabels.style,
            fontSize: `${this.labelSize}px`,
          },
        },
      };
    }
  }

  decreaseLabelSize() {
    if (this.labelSize > 10) {
      this.labelSize--;
      this.chartOptions = {
        ...this.chartOptions,
        dataLabels: {
          ...this.chartOptions.dataLabels,
          style: {
            ...this.chartOptions.dataLabels.style,
            fontSize: `${this.labelSize}px`,
          },
        },
      };
    }
  }
}
